import * as Yup from "yup";
import { t } from "i18next";

import { Genre } from "../personal-informations/personal-informations";

import { BodyCompositions } from "./body-compositions";
import { Cuisines } from "./cuisines";
import { NutritionGoals } from "./nutrition-goals";
import { NutritionObjectives } from "./nutrition-objectives";
import { NutritionTypes } from "./nutrition-types";
import { TrainingDurations } from "./training-durations";
import { WorkEffortLevels } from "./work-effort-levels";

export type NutritionWizardValues = {
  age?: number;
  genre?: Genre;
  weight?: number;
  height?: number;
  searchable?: number;
  cuisine?: Cuisines;
  nutritionType?: NutritionTypes;
  bodyComposition?: BodyCompositions;
  workEffortLevel?: WorkEffortLevels;
  trainingDuration?: TrainingDurations;
  nutritionGoal?: NutritionGoals;
  kcal?: number;
  objective?: NutritionObjectives;
  meals: any;
};

export const nutritionWizardInitialValues: NutritionWizardValues = {
  age: undefined,
  genre: undefined,
  weight: undefined,
  height: undefined,
  cuisine: undefined,
  searchable: 1,
  nutritionGoal: undefined,
  nutritionType: undefined,
  bodyComposition: undefined,
  workEffortLevel: undefined,
  trainingDuration: undefined,
  kcal: 0,
  objective: undefined,
  meals: undefined,
};

export const nutritionWizardValidationSchema = Yup.object({
  age: Yup.number()
    .required(t("contexts.nutrition.age_required"))
    .min(13, t("contexts.nutrition.age_min"))
    .max(100, t("contexts.nutrition.age_max")),
  genre: Yup.string().required(t("contexts.nutrition.select_genre_required")),
  weight: Yup.number()
    .required(t("contexts.nutrition.weight_required"))
    .min(50, t("contexts.nutrition.weight_min"))
    .max(200, t("contexts.nutrition.weight_max")),
  height: Yup.number()
    .required(t("contexts.nutrition.height_required"))
    .min(100, t("contexts.nutrition.height_min"))
    .max(250, t("contexts.nutrition.height_max")),
  cuisine: Yup.string().required(
    t("contexts.nutrition.select_cuisine_required")
  ),
  searchable: Yup.number().required().min(0).max(1),
  objective: Yup.string()
    .required(t("contexts.nutrition.select_training_objective_required"))
    .oneOf(Object.values(NutritionObjectives)),
  nutritionGoal: Yup.number().required(
    t("contexts.nutrition.select_nutritional_objective_required")
  ),
  nutritionType: Yup.string().required(
    t("contexts.nutrition.select_nutrition_type_required")
  ),
  bodyComposition: Yup.number().when(
    ["objective", "nutritionGoal"],
    ([objective, nutritionGoal], schema) => {
      const isHypertrophy = objective === NutritionObjectives.Hypertrophy;
      const isDefinition = objective === NutritionObjectives.Definition;
      const isWeightLoss =
        objective === NutritionObjectives.Health &&
        nutritionGoal === NutritionGoals.WeightLoss;

      if (isHypertrophy || isDefinition || isWeightLoss)
        return schema.required(
          t("contexts.nutrition.select_body_composition_required")
        );
      else return schema.notRequired();
    }
  ),
  workEffortLevel: Yup.number().when(
    ["objective", "nutritionGoal"],
    ([objective, nutritionGoal], schema) => {
      const isHypertrophy = objective === NutritionObjectives.Hypertrophy;
      const isDefinition = objective === NutritionObjectives.Definition;
      const isWeightLoss =
        objective === NutritionObjectives.Health &&
        nutritionGoal === NutritionGoals.WeightLoss;

      if (isHypertrophy || isDefinition || isWeightLoss)
        return schema.required(
          t("contexts.nutrition.select_work_effort_level_required")
        );
      else return schema.notRequired();
    }
  ),
  trainingDuration: Yup.number().when(
    ["objective", "nutritionGoal"],
    ([objective, nutritionGoal], schema) => {
      const isHypertrophy = objective === NutritionObjectives.Hypertrophy;
      const isDefinition = objective === NutritionObjectives.Definition;
      const isWeightLoss =
        objective === NutritionObjectives.Health &&
        nutritionGoal === NutritionGoals.WeightLoss;

      if (isHypertrophy || isDefinition || isWeightLoss)
        return schema.required(
          t("contexts.nutrition.select_training_duration_required")
        );
      else return schema.notRequired();
    }
  ),
  meals: Yup.object().when(
    ["objective", "nutritionGoal"],
    ([objective, nutritionGoal], schema) => {
      const isHypertrophy = objective === NutritionObjectives.Hypertrophy;
      const isDefinition = objective === NutritionObjectives.Definition;
      const isWeightLoss =
        objective === NutritionObjectives.Health &&
        nutritionGoal === NutritionGoals.WeightLoss;

      if (isHypertrophy || isDefinition || isWeightLoss)
        return schema.required(t("contexts.nutrition.select_meals_required"));
      else return schema.notRequired();
    }
  ),
});
